import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useViewer } from "@/hooks/useOrganizations";
import type { BadgeKind } from "@/lib/badges";

export const badgeKeys = {
  all: ["badges"] as const,
  forUser: (userId: string | undefined, viewer: string | null) => ["badges", "for-user", userId, viewer] as const,
  readyForHandle: (handle: string | undefined, profileId: string | undefined) =>
    ["badges", "ready-for-handle", handle, profileId] as const,
};

/** Every badge a member holds. member_badges is readable by signed-in members only. */
export function useMemberBadges(userId: string | undefined) {
  const { badges } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: badgeKeys.forUser(userId, viewer),
    queryFn: () => badges.listForUser(userId as string),
    enabled: !!userId && !!viewer,
  });
}

/** The `ready` badge's date for a logged-out visitor of /u/:handle. */
export function usePublicReadyBadge(handle: string | undefined, profileId: string | undefined, enabled = true) {
  const { badges } = useServices();
  return useQuery({
    queryKey: badgeKeys.readyForHandle(handle, profileId),
    queryFn: () => badges.readyForHandle(handle as string, profileId as string),
    enabled: enabled && !!handle && !!profileId,
  });
}

// Claims that came back true this session, per viewer. Every app load
// computes the checklists again; once the server has said yes there is
// nothing left to ask until the next load.
const claimed = new Set<string>();
const inFlight = new Set<string>();

/**
 * Claim `kind` once the caller's checklist reads `complete`. Members who
 * finished before badges existed get theirs the first time their list is
 * computed again. A false from the server (a step it can see is missing,
 * e.g. the client's view is stale) is not an error: the next time the list
 * completes it asks again. A failed call is dropped quietly too — a badge is
 * never worth an error toast over the checklist that earned it.
 */
export function useClaimBadgeWhenComplete(kind: BadgeKind, orgId: string | null, complete: boolean) {
  const { badges } = useServices();
  const viewer = useViewer();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!complete || !viewer) return;
    const key = `${viewer}:${kind}:${orgId ?? ""}`;
    if (claimed.has(key) || inFlight.has(key)) return;
    inFlight.add(key);
    badges
      .claim(kind, orgId ?? undefined)
      .then((ok) => {
        if (!ok) return;
        claimed.add(key);
        void queryClient.invalidateQueries({ queryKey: badgeKeys.all });
      })
      .catch(() => {})
      .finally(() => inFlight.delete(key));
  }, [badges, complete, kind, orgId, queryClient, viewer]);
}

export function __resetBadgeClaimsForTests(): void {
  claimed.clear();
  inFlight.clear();
}
